import { useState, useEffect, useMemo } from 'react';
import { apiFetch } from '../api';

const TABS = [
  { key: 'not_reviewed', label: 'Not Reviewed' },
  { key: 'reviewed', label: 'Reviewed' },
  { key: 'archived', label: 'Archived' },
];

function EmailBrowser({ refreshKey }) {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('not_reviewed');
  const [visibleCount, setVisibleCount] = useState(100);
  const [emailBody, setEmailBody] = useState(null);
  const [emailBodyEmail, setEmailBodyEmail] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [previewEmail, setPreviewEmail] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [savingPdf, setSavingPdf] = useState(false);

  useEffect(() => {
    fetchEmails();
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchEmails() {
    try {
      const res = await apiFetch('/api/emails');
      if (res.ok) setEmails(await res.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function updateStatus(messageId, status) {
    setEmails((prev) =>
      prev.map((r) => (r.messageId === messageId ? { ...r, status } : r))
    );
    try {
      await apiFetch('/api/emails/status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messageId, status }),
      });
    } catch (err) {
      console.error(err);
      fetchEmails();
    }
  }

  async function viewEmailBody(email) {
    setEmailBodyEmail(email);
    setEmailLoading(true);
    setEmailBody(null);
    try {
      const res = await apiFetch(
        `/api/emails/body?account=${encodeURIComponent(email.account)}&messageId=${encodeURIComponent(email.messageId)}`
      );
      if (res.ok) setEmailBody(await res.json());
    } catch (err) {
      console.error(err);
    } finally {
      setEmailLoading(false);
    }
  }

  function closeEmailBody() {
    setEmailBodyEmail(null);
    setEmailBody(null);
  }

  async function loadPreview(email, filename) {
    setPreviewEmail(email);
    setPreviewFile(filename);
    setPreviewLoading(true);
    setPreviewUrl(null);
    try {
      const url = `/api/emails/attachment?account=${encodeURIComponent(email.account)}&messageId=${encodeURIComponent(email.messageId)}&filename=${encodeURIComponent(filename)}`;
      const res = await apiFetch(url);
      if (res.ok) {
        const blob = await res.blob();
        setPreviewUrl(URL.createObjectURL(blob));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setPreviewLoading(false);
    }
  }

  function closePreview() {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewEmail(null);
    setPreviewFile(null);
    setPreviewUrl(null);
  }

  function switchPreviewFile(filename) {
    if (previewEmail) loadPreview(previewEmail, filename);
  }

  async function saveAsPdf() {
    if (!emailBody?.html || !emailBodyEmail) return;
    setSavingPdf(true);
    try {
      const { default: html2pdf } = await import('html2pdf.js');
      const container = document.createElement('div');
      container.innerHTML = emailBody.html;
      container.style.padding = '20px';
      document.body.appendChild(container);
      const filename = `${emailBodyEmail.subject.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 50)}.pdf`;
      await html2pdf().from(container).set({ filename, margin: 10, jsPDF: { format: 'letter' } }).save();
      document.body.removeChild(container);
    } catch (err) {
      console.error(err);
    } finally {
      setSavingPdf(false);
    }
  }

  function isPdf(filename) { return filename?.toLowerCase().endsWith('.pdf'); }
  function isImage(filename) { return /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(filename || ''); }

  const filtered = useMemo(() => {
    let list = emails.filter((r) => (r.status || 'not_reviewed') === activeTab);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((r) =>
        (r.subject && r.subject.toLowerCase().includes(q)) ||
        (r.from && r.from.toLowerCase().includes(q)) ||
        (r.attachmentNames && r.attachmentNames.some((n) => n.toLowerCase().includes(q)))
      );
    }
    return list.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [emails, activeTab, search]);

  const tabCounts = useMemo(() => {
    const counts = {};
    TABS.forEach((t) => {
      counts[t.key] = emails.filter((r) => (r.status || 'not_reviewed') === t.key).length;
    });
    return counts;
  }, [emails]);

  if (loading) {
    return <section className="card"><h2>Email Browser</h2><p className="loading">Loading...</p></section>;
  }

  return (
    <section className="card receipts-section">
      <h2 style={{ margin: '0 0 8px' }}>Email Browser <span style={{ fontSize: 13, fontWeight: 400, color: '#71717a' }}>({emails.length} emails)</span></h2>

      <div className="receipts-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            className={`receipts-tab${activeTab === tab.key ? ' active' : ''}`}
            onClick={() => { setActiveTab(tab.key); setVisibleCount(100); }}
          >
            {tab.label} ({tabCounts[tab.key] || 0})
          </button>
        ))}
      </div>

      <input
        type="text"
        value={search}
        onChange={(e) => { setSearch(e.target.value); setVisibleCount(100); }}
        placeholder="Search emails by subject, sender, or attachment..."
        className="sfin-tx-search"
        style={{ marginBottom: 8 }}
      />

      <div className="receipts-list">
        {filtered.length === 0 && <div className="receipts-empty">No emails found</div>}
        {filtered.slice(0, visibleCount).map((r) => (
          <div key={r.messageId} className="receipt-item">
            <div className="receipt-info">
              <span className="receipt-subject">{r.subject}</span>
              <span className="receipt-from">{r.from}</span>
            </div>
            <div className="receipt-meta">
              <span className="receipt-date">
                {new Date(r.date).toLocaleDateString('en-US', {
                  timeZone: 'America/Chicago', month: 'short', day: 'numeric', year: 'numeric',
                })}
              </span>
              {r.hasAttachments && (
                <span className="receipt-attachment-badge">
                  {r.attachmentNames?.length || 0} file{(r.attachmentNames?.length || 0) !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            <div className="receipt-actions">
              <button className="receipt-action-btn view-btn" onClick={() => viewEmailBody(r)}>View</button>
              {r.hasAttachments && r.attachmentNames?.length > 0 && (
                <button className="receipt-action-btn view-btn" onClick={() => loadPreview(r, r.attachmentNames[0])}>
                  Attachments
                </button>
              )}
              {activeTab !== 'not_reviewed' && (
                <button className="receipt-action-btn review-btn" onClick={() => updateStatus(r.messageId, 'not_reviewed')}>Not Reviewed</button>
              )}
              {activeTab !== 'reviewed' && (
                <button className="receipt-action-btn import-btn" onClick={() => updateStatus(r.messageId, 'reviewed')}>Reviewed</button>
              )}
              {activeTab !== 'archived' && (
                <button className="receipt-action-btn ignore-btn" onClick={() => updateStatus(r.messageId, 'archived')}>Archive</button>
              )}
            </div>
          </div>
        ))}
      </div>

      {visibleCount < filtered.length && (
        <button className="receipts-load-more" onClick={() => setVisibleCount((c) => c + 100)}>
          Load More ({filtered.length - visibleCount} remaining)
        </button>
      )}

      {/* Attachment Preview Modal */}
      {previewEmail && (
        <div className="modal-overlay" onClick={closePreview}>
          <div className="modal-content receipt-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="receipt-preview-header">
              <h2>{previewFile}</h2>
              {previewEmail.attachmentNames.length > 1 && (
                <div className="receipt-preview-file-selector">
                  {previewEmail.attachmentNames.map((fn) => (
                    <button key={fn} className={`receipt-file-tab${fn === previewFile ? ' active' : ''}`}
                      onClick={() => switchPreviewFile(fn)}>{fn}</button>
                  ))}
                </div>
              )}
              <button className="modal-close" onClick={closePreview}>&times;</button>
            </div>
            <div className="receipt-preview-body">
              {previewLoading && <p className="loading">Loading preview...</p>}
              {!previewLoading && previewUrl && isPdf(previewFile) && (
                <iframe src={previewUrl} title={previewFile} className="receipt-preview-iframe" />
              )}
              {!previewLoading && previewUrl && isImage(previewFile) && (
                <img src={previewUrl} alt={previewFile} className="receipt-preview-image" />
              )}
              {!previewLoading && previewUrl && !isPdf(previewFile) && !isImage(previewFile) && (
                <div className="receipt-preview-unsupported">
                  <p>Preview not available.</p>
                  <a href={previewUrl} download={previewFile} className="receipt-download-link">Download {previewFile}</a>
                </div>
              )}
              {!previewLoading && !previewUrl && <p className="empty">Could not load attachment</p>}
            </div>
          </div>
        </div>
      )}

      {/* Email Body Modal */}
      {emailBodyEmail && (
        <div className="modal-overlay" onClick={closeEmailBody}>
          <div className="modal-content receipt-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="receipt-preview-header">
              <h2>{emailBodyEmail.subject}</h2>
              <button className="receipt-action-btn import-btn" onClick={saveAsPdf}
                disabled={savingPdf || emailLoading || !emailBody} style={{ flexShrink: 0 }}>
                {savingPdf ? 'Saving...' : 'Save as PDF'}
              </button>
              <button className="modal-close" onClick={closeEmailBody}>&times;</button>
            </div>
            <div className="receipt-email-meta">
              <span>From: {emailBodyEmail.from}</span>
              <span>Date: {new Date(emailBodyEmail.date).toLocaleDateString('en-US', {
                timeZone: 'America/Chicago', month: 'short', day: 'numeric', year: 'numeric',
                hour: 'numeric', minute: '2-digit',
              })}</span>
            </div>
            <div className="receipt-preview-body">
              {emailLoading && <p className="loading">Loading email...</p>}
              {!emailLoading && emailBody?.html && (
                <iframe srcDoc={emailBody.html} title="Email body" className="receipt-email-iframe" sandbox="allow-same-origin" />
              )}
              {!emailLoading && !emailBody?.html && emailBody?.text && (
                <pre className="receipt-email-text">{emailBody.text}</pre>
              )}
              {!emailLoading && !emailBody && (
                <p className="empty">Could not load email body. Account may need re-authorization.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default EmailBrowser;
